import React, { useState, useEffect } from 'react';
import { BASE_URL } from '../../config/constants';
import { generateToken, TOKEN_HEADER } from '../../utils/tokenUtils';

const Quizzes = () => {
  const [isSliderOpen, setIsSliderOpen] = useState(false);
  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    courseId: '',
    description: '',
    instructions: '',
    time_limit: 30,
    passing_score: 70,
    max_attempts: 3,
    shuffle_questions: true,
    show_correct_answers: true,
    show_results_immediately: true,
    is_required: false
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [currentQuiz, setCurrentQuiz] = useState(null);
  const [showQuestionsView, setShowQuestionsView] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [isAddingQuestion, setIsAddingQuestion] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState({
    questionText: '',
    questionType: 'single_choice',
    options: ['', ''],
    correctAnswer: '',
    correctAnswers: [],
    points: 1
  });
  const [selectedCourseFilter, setSelectedCourseFilter] = useState('');
  const [quizzesList, setQuizzesList] = useState([]);
  const [loadingQuizzes, setLoadingQuizzes] = useState(false);

  useEffect(() => {
    if (isSliderOpen) {
      fetchCourses();
    }
  }, [isSliderOpen]);

  useEffect(() => {
    // Fetch courses on component mount for the filter dropdown
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    setLoadingCourses(true);
    try {
      const response = await fetch(`${BASE_URL}/courses`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          [TOKEN_HEADER]: generateToken(),
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setCourses(result.data);
        }
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setLoadingCourses(false);
    }
  };

  const fetchQuizzesByCourse = async (courseId) => {
    if (!courseId) {
      setQuizzesList([]);
      return;
    }

    setLoadingQuizzes(true);
    try {
      const response = await fetch(`${BASE_URL}/quizzes/course/${courseId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          [TOKEN_HEADER]: generateToken(),
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setQuizzesList(result.data);
        } else {
          setQuizzesList([]);
        }
      } else {
        setQuizzesList([]);
      }
    } catch (error) {
      console.error('Error fetching quizzes:', error);
      setQuizzesList([]);
    } finally {
      setLoadingQuizzes(false);
    }
  };

  const handleCourseFilterChange = (courseId) => {
    setSelectedCourseFilter(courseId);
    fetchQuizzesByCourse(courseId);
  };

  const handleOpenSlider = () => {
    setIsSliderOpen(true);
    setFormData({
      title: '',
      courseId: '',
      description: '',
      instructions: '',
      time_limit: 30,
      passing_score: 70,
      max_attempts: 3,
      shuffle_questions: true,
      show_correct_answers: true,
      show_results_immediately: true,
      is_required: false
    });
    setErrors({});
  };

  const handleCloseSlider = () => {
    setIsSliderOpen(false);
    setFormData({
      title: '',
      courseId: '',
      description: '',
      instructions: '',
      time_limit: 30,
      passing_score: 70,
      max_attempts: 3,
      shuffle_questions: true,
      show_correct_answers: true,
      show_results_immediately: true,
      is_required: false
    });
    setErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Quiz title is required';
    }
    
    if (!formData.courseId) {
      newErrors.courseId = 'Please select a course';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.instructions.trim()) {
      newErrors.instructions = 'Instructions are required';
    }

    if (formData.passing_score < 0 || formData.passing_score > 100) {
      newErrors.passing_score = 'Passing score must be between 0 and 100';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      // Save quiz locally instead of API call
      const newQuiz = {
        id: Date.now(), // Temporary ID
        title: formData.title,
        courseId: parseInt(formData.courseId),
        description: formData.description,
        instructions: formData.instructions,
        time_limit: parseInt(formData.time_limit),
        passing_score: parseInt(formData.passing_score),
        max_attempts: parseInt(formData.max_attempts),
        shuffle_questions: formData.shuffle_questions,
        show_correct_answers: formData.show_correct_answers,
        show_results_immediately: formData.show_results_immediately,
        is_required: formData.is_required,
        questions: []
      };

      setCurrentQuiz(newQuiz);
      setQuestions([]);
      handleCloseSlider();
      setShowQuestionsView(true);
    } catch (error) {
      console.error('Error creating quiz:', error);
      alert('Error creating quiz. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddNewQuestion = () => {
    setIsAddingQuestion(true);
    setCurrentQuestion({
      questionText: '',
      questionType: 'single_choice',
      options: ['', ''],
      correctAnswer: '',
      correctAnswers: [],
      points: 1
    });
  };

  const handleQuestionTypeChange = (type) => {
    setCurrentQuestion(prev => ({
      ...prev,
      questionType: type,
      options: type === 'free_text' ? [] : ['', ''],
      correctAnswer: '',
      correctAnswers: []
    }));
  };

  const handleAddOption = () => {
    setCurrentQuestion(prev => ({
      ...prev,
      options: [...prev.options, '']
    }));
  };

  const handleRemoveOption = (index) => {
    setCurrentQuestion(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  const handleOptionChange = (index, value) => {
    setCurrentQuestion(prev => ({
      ...prev,
      options: prev.options.map((opt, i) => i === index ? value : opt)
    }));
  };

  const handleSaveQuestion = () => {
    // Validate question
    if (!currentQuestion.questionText.trim()) {
      alert('Question text is required');
      return;
    }

    if (currentQuestion.questionType !== 'free_text') {
      if (currentQuestion.options.some(opt => !opt.trim())) {
        alert('All options must be filled');
        return;
      }

      if (currentQuestion.questionType === 'single_choice' && !currentQuestion.correctAnswer) {
        alert('Please select a correct answer');
        return;
      }

      if (currentQuestion.questionType === 'multiple_choice' && currentQuestion.correctAnswers.length === 0) {
        alert('Please select at least one correct answer');
        return;
      }
    }

    const newQuestion = {
      ...currentQuestion,
      id: Date.now()
    };

    setQuestions(prev => [...prev, newQuestion]);
    setIsAddingQuestion(false);
    setCurrentQuestion({
      questionText: '',
      questionType: 'single_choice',
      options: ['', ''],
      correctAnswer: '',
      correctAnswers: [],
      points: 1
    });
  };

  const handleCancelQuestion = () => {
    setIsAddingQuestion(false);
    setCurrentQuestion({
      questionText: '',
      questionType: 'single_choice',
      options: ['', ''],
      correctAnswer: '',
      correctAnswers: [],
      points: 1
    });
  };

  const handleFinishQuiz = async () => {
    if (questions.length === 0) {
      alert('Please add at least one question');
      return;
    }

    try {
      // Format questions according to API structure
      const formattedQuestions = questions.map((q, index) => {
        const baseQuestion = {
          question_text: q.questionText,
          question_type: q.questionType === 'single_choice' ? 'multiple_choice' : 
                        q.questionType === 'multiple_choice' ? 'multiple_choice' : 
                        q.questionType === 'free_text' ? 'short_answer' : q.questionType,
          points: q.points,
          display_order: index + 1,
          explanation: q.explanation || ''
        };

        // Format options based on question type
        if (q.questionType === 'free_text') {
          baseQuestion.options = [];
        } else {
          baseQuestion.options = q.options.map((option, optIndex) => ({
            option_text: option,
            is_correct: q.questionType === 'single_choice' 
              ? q.correctAnswer === option
              : q.correctAnswers.includes(option),
            display_order: optIndex + 1
          }));
        }

        return baseQuestion;
      });

      // Format complete quiz data
      const quizData = {
        course_id: currentQuiz.courseId,
        title: currentQuiz.title,
        description: currentQuiz.description,
        instructions: currentQuiz.instructions,
        time_limit: currentQuiz.time_limit,
        passing_score: currentQuiz.passing_score,
        max_attempts: currentQuiz.max_attempts,
        shuffle_questions: currentQuiz.shuffle_questions,
        show_correct_answers: currentQuiz.show_correct_answers,
        show_results_immediately: currentQuiz.show_results_immediately,
        is_required: currentQuiz.is_required,
        questions: formattedQuestions
      };

      console.log('Submitting quiz data:', JSON.stringify(quizData, null, 2));

      const response = await fetch(`${BASE_URL}/quizzes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          [TOKEN_HEADER]: generateToken(),
        },
        body: JSON.stringify(quizData)
      });

      const result = await response.json();

      if (response.ok && result.success) {
        alert('Quiz created successfully with all questions!');
        setShowQuestionsView(false);
        setCurrentQuiz(null);
        setQuestions([]);
      } else {
        alert(result.message || 'Failed to create quiz');
      }
    } catch (error) {
      console.error('Error saving quiz:', error);
      alert('Error saving quiz. Please try again.');
    }
  };

  const handleBackToQuizList = () => {
    if (questions.length > 0) {
      if (!window.confirm('You have unsaved questions. Are you sure you want to go back?')) {
        return;
      }
    }
    setShowQuestionsView(false);
    setCurrentQuiz(null);
    setQuestions([]);
  };

  const handleMoveQuestionUp = (index) => {
    if (index === 0) return;
    const newQuestions = [...questions];
    [newQuestions[index - 1], newQuestions[index]] = [newQuestions[index], newQuestions[index - 1]];
    setQuestions(newQuestions);
  };

  const handleMoveQuestionDown = (index) => {
    if (index === questions.length - 1) return;
    const newQuestions = [...questions];
    [newQuestions[index], newQuestions[index + 1]] = [newQuestions[index + 1], newQuestions[index]];
    setQuestions(newQuestions);
  };

  const handleDeleteQuestion = (index) => {
    if (window.confirm('Are you sure you want to delete this question?')) {
      setQuestions(prev => prev.filter((_, i) => i !== index));
    }
  };

  return (
    <div className="section-content">
      {!showQuestionsView ? (
        <>
          <div className="section-header">
            <h2>Quizzes Management</h2>
            <div className="header-controls">
              <div className="course-filter-wrapper">
                <select
                  id="courseFilter"
                  value={selectedCourseFilter}
                  onChange={(e) => handleCourseFilterChange(e.target.value)}
                  className="course-filter-select"
                >
                  <option value="">📚 All Courses</option>
                  {courses.map(course => (
                    <option key={course.id} value={course.id}>
                      {course.course_code} - {course.title}
                    </option>
                  ))}
                </select>
              </div>
              <div className="section-actions">
                <button className="action-btn action-btn-primary" onClick={handleOpenSlider}>
                  ➕ Create New Quiz
                </button>
                <button className="action-btn action-btn-secondary">📊 View Statistics</button>
              </div>
            </div>
          </div>

          {loadingQuizzes ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading quizzes...</p>
            </div>
          ) : selectedCourseFilter && quizzesList.length === 0 ? (
            <div className="placeholder-content">
              <div className="placeholder-icon">📝</div>
              <h3>No Quizzes Found</h3>
              <p>No quizzes have been created for this course yet.</p>
              <button className="action-btn action-btn-primary" style={{ marginTop: '20px' }} onClick={handleOpenSlider}>
                ➕ Create First Quiz
              </button>
            </div>
          ) : selectedCourseFilter && quizzesList.length > 0 ? (
            <div className="table-container">
              <table className="quizzes-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Title</th>
                    <th>Description</th>
                    <th>Time Limit</th>
                    <th>Passing Score</th>
                    <th>Max Attempts</th>
                    <th>Required</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {quizzesList.map((quiz) => (
                    <tr key={quiz.id}>
                      <td>{quiz.id}</td>
                      <td>
                        <strong>{quiz.title}</strong>
                      </td>
                      <td className="description-cell">{quiz.description}</td>
                      <td>{quiz.time_limit} min</td>
                      <td>{quiz.passing_score}%</td>
                      <td>{quiz.max_attempts}</td>
                      <td>
                        <span className={`badge ${quiz.is_required ? 'badge-required' : 'badge-optional'}`}>
                          {quiz.is_required ? '✓ Required' : 'Optional'}
                        </span>
                      </td>
                      <td>{new Date(quiz.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</td>
                      <td>
                        <div className="action-buttons">
                          <button className="action-btn-small" title="View Quiz">👁️</button>
                          <button className="action-btn-small" title="Edit Quiz">✏️</button>
                          <button className="action-btn-small delete" title="Delete Quiz">🗑️</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="placeholder-content">
              <div className="placeholder-icon">📝</div>
              <h3>Quizzes Management</h3>
              <p>Select a course to view its quizzes or create a new quiz.</p>
              <ul className="feature-list">
                <li>✓ Create quizzes with multiple question types</li>
                <li>✓ Add questions and answer options</li>
                <li>✓ Set passing scores and time limits</li>
                <li>✓ Link quizzes to courses/lessons</li>
                <li>✓ View quiz attempts and scores</li>
              </ul>
            </div>
          )}
        </>
      ) : (
        <div className="questions-view">
          <div className="section-header">
            <div>
              <h2>{currentQuiz?.title}</h2>
              <p style={{ margin: '8px 0 0 0', color: '#666', fontSize: '14px' }}>
                {currentQuiz?.description}
              </p>
            </div>
            <div className="section-actions">
              <button className="action-btn action-btn-secondary" onClick={handleBackToQuizList}>
                ← Back
              </button>
              <button className="action-btn action-btn-primary" onClick={handleFinishQuiz}>
                💾 Save Quiz
              </button>
            </div>
          </div>

          <div className="questions-list">
            <h3>Questions ({questions.length})</h3>
            {questions.map((question, index) => (
              <div key={question.id} className="question-card">
                <div className="question-header">
                  <span className="question-number">Q{index + 1}</span>
                  <span className="question-type-badge">
                    {question.questionType === 'free_text' ? '✍️ Free Text' :
                     question.questionType === 'single_choice' ? '⭕ Single Choice' :
                     '☑️ Multiple Choice'}
                  </span>
                  <span className="question-points">{question.points} pts</span>
                  <div className="question-actions">
                    <button
                      className="move-btn move-up"
                      onClick={() => handleMoveQuestionUp(index)}
                      disabled={index === 0}
                      title="Move up"
                    >
                      ▲
                    </button>
                    <button
                      className="move-btn move-down"
                      onClick={() => handleMoveQuestionDown(index)}
                      disabled={index === questions.length - 1}
                      title="Move down"
                    >
                      ▼
                    </button>
                    <button
                      className="delete-btn"
                      onClick={() => handleDeleteQuestion(index)}
                      title="Delete question"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
                <p className="question-text">{question.questionText}</p>
                {question.questionType !== 'free_text' && (
                  <div className="question-options">
                    {question.options.map((option, optIndex) => (
                      <div key={optIndex} className="option-preview">
                        <span className={
                          question.questionType === 'single_choice' 
                            ? (question.correctAnswer === option ? 'correct-indicator' : 'option-indicator')
                            : (question.correctAnswers.includes(option) ? 'correct-indicator' : 'option-indicator')
                        }>
                          {question.questionType === 'single_choice' 
                            ? (question.correctAnswer === option ? '✓' : '○')
                            : (question.correctAnswers.includes(option) ? '✓' : '☐')}
                        </span>
                        <span>{option}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {!isAddingQuestion && (
              <button className="add-question-btn" onClick={handleAddNewQuestion}>
                ➕ Add New Question
              </button>
            )}
          </div>

          {isAddingQuestion && (
            <div className="question-form-overlay">
              <div className="question-form-panel">
                <div className="question-form-header">
                  <h3>Add New Question</h3>
                  <button className="close-btn" onClick={handleCancelQuestion}>✕</button>
                </div>

                <div className="question-form-body">
                  <div className="form-group">
                    <label>Question Type *</label>
                    <select
                      value={currentQuestion.questionType}
                      onChange={(e) => handleQuestionTypeChange(e.target.value)}
                    >
                      <option value="single_choice">Single Choice</option>
                      <option value="multiple_choice">Multiple Choice</option>
                      <option value="free_text">Free Text</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Question Text *</label>
                    <textarea
                      value={currentQuestion.questionText}
                      onChange={(e) => setCurrentQuestion(prev => ({ ...prev, questionText: e.target.value }))}
                      placeholder="Enter your question here..."
                      rows="3"
                    />
                  </div>

                  <div className="form-group">
                    <label>Points</label>
                    <input
                      type="number"
                      min="1"
                      value={currentQuestion.points}
                      onChange={(e) => setCurrentQuestion(prev => ({ ...prev, points: parseInt(e.target.value) || 1 }))}
                    />
                  </div>

                  {currentQuestion.questionType !== 'free_text' && (
                    <>
                      <div className="form-group">
                        <label>Answer Options *</label>
                        {currentQuestion.options.map((option, index) => (
                          <div key={index} className="option-input-group">
                            {currentQuestion.questionType === 'single_choice' ? (
                              <input
                                type="radio"
                                name="correctAnswer"
                                checked={currentQuestion.correctAnswer === option}
                                onChange={() => setCurrentQuestion(prev => ({ ...prev, correctAnswer: option }))}
                              />
                            ) : (
                              <input
                                type="checkbox"
                                checked={currentQuestion.correctAnswers.includes(option)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setCurrentQuestion(prev => ({
                                      ...prev,
                                      correctAnswers: [...prev.correctAnswers, option]
                                    }));
                                  } else {
                                    setCurrentQuestion(prev => ({
                                      ...prev,
                                      correctAnswers: prev.correctAnswers.filter(ans => ans !== option)
                                    }));
                                  }
                                }}
                              />
                            )}
                            <input
                              type="text"
                              value={option}
                              onChange={(e) => handleOptionChange(index, e.target.value)}
                              placeholder={`Option ${index + 1}`}
                            />
                            {currentQuestion.options.length > 2 && (
                              <button
                                type="button"
                                className="remove-option-btn"
                                onClick={() => handleRemoveOption(index)}
                              >
                                ✕
                              </button>
                            )}
                          </div>
                        ))}
                        <button type="button" className="add-option-btn" onClick={handleAddOption}>
                          ➕ Add Option
                        </button>
                      </div>
                    </>
                  )}
                </div>

                <div className="question-form-footer">
                  <button className="btn-secondary" onClick={handleCancelQuestion}>Cancel</button>
                  <button className="btn-primary" onClick={handleSaveQuestion}>Save Question</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Slider Overlay */}
      {isSliderOpen && (
        <div className="slider-overlay" onClick={handleCloseSlider}>
          <div 
            className={`slider-panel ${isSliderOpen ? 'open' : ''}`} 
            onClick={(e) => e.stopPropagation()}
          >
            <div className="slider-header">
              <h2>Create New Quiz</h2>
              <button className="close-slider-btn" onClick={handleCloseSlider}>
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="slider-form">
              <div className="form-group">
                <label htmlFor="title">Quiz Title *</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Enter quiz title"
                  className={errors.title ? 'error' : ''}
                />
                {errors.title && <span className="error-message">{errors.title}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="courseId">Link to Course *</label>
                {loadingCourses ? (
                  <div className="loading-select">Loading courses...</div>
                ) : (
                  <select
                    id="courseId"
                    name="courseId"
                    value={formData.courseId}
                    onChange={handleInputChange}
                    className={errors.courseId ? 'error' : ''}
                  >
                    <option value="">Select a course</option>
                    {courses.map(course => (
                      <option key={course.id} value={course.id}>
                        {course.course_code} - {course.title}
                      </option>
                    ))}
                  </select>
                )}
                {errors.courseId && <span className="error-message">{errors.courseId}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="description">Description *</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter quiz description"
                  rows="3"
                  className={errors.description ? 'error' : ''}
                ></textarea>
                {errors.description && <span className="error-message">{errors.description}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="instructions">Instructions *</label>
                <textarea
                  id="instructions"
                  name="instructions"
                  value={formData.instructions}
                  onChange={handleInputChange}
                  placeholder="Enter quiz instructions (e.g., Choose the best answer for each question)"
                  rows="2"
                  className={errors.instructions ? 'error' : ''}
                ></textarea>
                {errors.instructions && <span className="error-message">{errors.instructions}</span>}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="time_limit">Time Limit (minutes)</label>
                  <input
                    type="number"
                    id="time_limit"
                    name="time_limit"
                    value={formData.time_limit}
                    onChange={handleInputChange}
                    min="1"
                    placeholder="30"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="passing_score">Passing Score (%)</label>
                  <input
                    type="number"
                    id="passing_score"
                    name="passing_score"
                    value={formData.passing_score}
                    onChange={handleInputChange}
                    min="0"
                    max="100"
                    placeholder="70"
                    className={errors.passing_score ? 'error' : ''}
                  />
                  {errors.passing_score && <span className="error-message">{errors.passing_score}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="max_attempts">Max Attempts</label>
                  <input
                    type="number"
                    id="max_attempts"
                    name="max_attempts"
                    value={formData.max_attempts}
                    onChange={handleInputChange}
                    min="1"
                    placeholder="3"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="shuffle_questions"
                    checked={formData.shuffle_questions}
                    onChange={(e) => handleInputChange({ target: { name: 'shuffle_questions', value: e.target.checked } })}
                  />
                  <span>Shuffle Questions</span>
                </label>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="show_correct_answers"
                    checked={formData.show_correct_answers}
                    onChange={(e) => handleInputChange({ target: { name: 'show_correct_answers', value: e.target.checked } })}
                  />
                  <span>Show Correct Answers After Submission</span>
                </label>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="show_results_immediately"
                    checked={formData.show_results_immediately}
                    onChange={(e) => handleInputChange({ target: { name: 'show_results_immediately', value: e.target.checked } })}
                  />
                  <span>Show Results Immediately</span>
                </label>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="is_required"
                    checked={formData.is_required}
                    onChange={(e) => handleInputChange({ target: { name: 'is_required', value: e.target.checked } })}
                  />
                  <span>Required Quiz</span>
                </label>
              </div>

              <div className="slider-footer">
                <button type="button" className="btn-secondary" onClick={handleCloseSlider}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={submitting}>
                  {submitting ? 'Creating...' : 'Create Quiz'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style jsx>{`
        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 2px solid #e5e7eb;
          background: white;
          flex-wrap: wrap;
          gap: 16px;
        }

        .section-header h2 {
          margin: 0;
          font-size: 24px;
          color: #1f2937;
          font-weight: 700;
        }

        .header-controls {
          display: flex;
          align-items: center;
          gap: 16px;
          flex-wrap: wrap;
        }

        .course-filter-wrapper {
          position: relative;
        }

        .course-filter-select {
          padding: 10px 16px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          min-width: 280px;
          cursor: pointer;
          transition: all 0.3s ease;
          background: white;
          color: #374151;
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23374151' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 12px center;
          padding-right: 40px;
        }

        .course-filter-select:hover {
          border-color: #d1d5db;
          background-color: #f9fafb;
        }

        .course-filter-select:focus {
          outline: none;
          border-color: #b91c1c;
          box-shadow: 0 0 0 3px rgba(185, 28, 28, 0.1);
        }

        .section-actions {
          display: flex;
          gap: 12px;
          align-items: center;
        }

        @media (max-width: 768px) {
          .section-header {
            flex-direction: column;
            align-items: flex-start;
          }

          .header-controls {
            width: 100%;
            flex-direction: column;
            align-items: stretch;
          }

          .course-filter-select {
            width: 100%;
            min-width: auto;
          }

          .section-actions {
            width: 100%;
            flex-direction: column;
          }

          .section-actions button {
            width: 100%;
          }
        }

        .filter-section {
          padding: 20px 24px;
          background: #f9fafb;
          border-bottom: 1px solid #e5e7eb;
        }

        .filter-group {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .filter-group label {
          font-weight: 600;
          color: #374151;
          font-size: 14px;
        }

        .loading-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
          gap: 16px;
        }

        .spinner {
          width: 48px;
          height: 48px;
          border: 4px solid #fecaca;
          border-top-color: #b91c1c;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .loading-state p {
          color: #6b7280;
          font-size: 16px;
          font-weight: 500;
        }

        .table-container {
          padding: 24px;
          overflow-x: auto;
        }

        .quizzes-table {
          width: 100%;
          border-collapse: collapse;
          background: white;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .quizzes-table thead {
          background: linear-gradient(135deg, #b91c1c 0%, #dc2626 100%);
          color: white;
        }

        .quizzes-table th {
          padding: 14px 16px;
          text-align: left;
          font-weight: 600;
          font-size: 13px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .quizzes-table td {
          padding: 14px 16px;
          border-bottom: 1px solid #e5e7eb;
          font-size: 14px;
          color: #374151;
        }

        .quizzes-table tbody tr:hover {
          background: #f9fafb;
        }

        .quizzes-table tbody tr:last-child td {
          border-bottom: none;
        }

        .description-cell {
          max-width: 300px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .badge {
          display: inline-block;
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 600;
        }

        .badge-required {
          background: #dcfce7;
          color: #166534;
        }

        .badge-optional {
          background: #e5e7eb;
          color: #6b7280;
        }

        .action-buttons {
          display: flex;
          gap: 8px;
        }

        .action-btn-small {
          padding: 6px 10px;
          background: #f3f4f6;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.3s ease;
        }

        .action-btn-small:hover {
          background: #e5e7eb;
          transform: translateY(-2px);
        }

        .action-btn-small.delete {
          background: #fee2e2;
        }

        .action-btn-small.delete:hover {
          background: #fecaca;
        }

        .slider-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 1000;
          display: flex;
          justify-content: flex-end;
          animation: fadeIn 0.3s ease;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .slider-panel {
          width: 500px;
          max-width: 90%;
          background: white;
          box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
          display: flex;
          flex-direction: column;
          animation: slideIn 0.3s ease;
          overflow-y: auto;
        }

        @keyframes slideIn {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }

        .slider-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 24px;
          border-bottom: 1px solid #e5e7eb;
          background: linear-gradient(135deg, #b91c1c 0%, #dc2626 100%);
          color: white;
        }

        .slider-header h2 {
          margin: 0;
          font-size: 24px;
          font-weight: 700;
        }

        .close-slider-btn {
          background: rgba(255, 255, 255, 0.2);
          border: none;
          color: white;
          font-size: 24px;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
        }

        .close-slider-btn:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: rotate(90deg);
        }

        .slider-form {
          padding: 24px;
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .form-group {
          margin-bottom: 24px;
        }

        .form-group label {
          display: block;
          margin-bottom: 8px;
          font-weight: 600;
          color: #374151;
          font-size: 14px;
        }

        .form-group input,
        .form-group select,
        .form-group textarea {
          width: 100%;
          padding: 12px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          font-size: 14px;
          transition: all 0.3s ease;
          font-family: inherit;
        }

        .form-group input:focus,
        .form-group select:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: #b91c1c;
          box-shadow: 0 0 0 3px rgba(185, 28, 28, 0.1);
        }

        .form-group input.error,
        .form-group select.error,
        .form-group textarea.error {
          border-color: #ef4444;
        }

        .form-group textarea {
          resize: vertical;
          min-height: 100px;
        }

        .form-row {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 10px;
          cursor: pointer;
          font-weight: 500;
          color: #374151;
        }

        .checkbox-label input[type="checkbox"] {
          width: 20px;
          height: 20px;
          cursor: pointer;
        }

        .checkbox-label span {
          font-weight: 500;
        }

        @media (max-width: 768px) {
          .form-row {
            grid-template-columns: 1fr;
          }
        }

        .loading-select {
          padding: 12px;
          background: #f3f4f6;
          border-radius: 8px;
          color: #6b7280;
          text-align: center;
        }

        .error-message {
          display: block;
          margin-top: 6px;
          color: #ef4444;
          font-size: 13px;
        }

        .slider-footer {
          display: flex;
          gap: 12px;
          padding-top: 24px;
          border-top: 1px solid #e5e7eb;
          margin-top: auto;
        }

        .btn-primary,
        .btn-secondary {
          flex: 1;
          padding: 12px 24px;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .btn-primary {
          background: linear-gradient(135deg, #b91c1c 0%, #dc2626 100%);
          color: white;
        }

        .btn-primary:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(185, 28, 28, 0.3);
        }

        .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-secondary {
          background: #f3f4f6;
          color: #374151;
        }

        .btn-secondary:hover {
          background: #e5e7eb;
        }

        @media (max-width: 768px) {
          .slider-panel {
            width: 100%;
            max-width: 100%;
          }
        }

        /* Questions View Styles */
        .questions-view {
          width: 100%;
        }

        .questions-list {
          padding: 24px;
        }

        .questions-list h3 {
          margin: 0 0 20px 0;
          font-size: 20px;
          color: #374151;
        }

        .question-card {
          background: white;
          border: 2px solid #e5e7eb;
          border-radius: 12px;
          padding: 20px;
          margin-bottom: 16px;
          transition: all 0.3s ease;
        }

        .question-card:hover {
          border-color: #b91c1c;
          box-shadow: 0 4px 12px rgba(185, 28, 28, 0.1);
        }

        .question-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 12px;
        }

        .question-number {
          background: linear-gradient(135deg, #b91c1c 0%, #dc2626 100%);
          color: white;
          padding: 4px 12px;
          border-radius: 6px;
          font-weight: 600;
          font-size: 14px;
        }

        .question-type-badge {
          background: #f3f4f6;
          color: #374151;
          padding: 4px 12px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 500;
        }

        .question-points {
          background: #fef3c7;
          color: #92400e;
          padding: 4px 12px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 600;
        }

        .question-actions {
          margin-left: auto;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .move-btn {
          background: #f3f4f6;
          border: none;
          color: #374151;
          width: 32px;
          height: 32px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
        }

        .move-btn:hover:not(:disabled) {
          background: #e5e7eb;
          color: #b91c1c;
        }

        .move-btn:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }

        .delete-btn {
          background: #fee2e2;
          border: none;
          color: #991b1b;
          width: 32px;
          height: 32px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
        }

        .delete-btn:hover {
          background: #fecaca;
        }

        .question-text {
          margin: 12px 0;
          font-size: 16px;
          color: #1f2937;
          font-weight: 500;
        }

        .question-options {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-top: 12px;
        }

        .option-preview {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px;
          background: #f9fafb;
          border-radius: 8px;
          font-size: 14px;
        }

        .correct-indicator {
          color: #10b981;
          font-weight: 700;
          font-size: 16px;
        }

        .option-indicator {
          color: #9ca3af;
          font-size: 16px;
        }

        .add-question-btn {
          width: 100%;
          padding: 16px;
          background: white;
          border: 2px dashed #d1d5db;
          border-radius: 12px;
          color: #6b7280;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .add-question-btn:hover {
          border-color: #b91c1c;
          color: #b91c1c;
          background: #fef2f2;
        }

        /* Question Form Overlay */
        .question-form-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 2000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          animation: fadeIn 0.3s ease;
        }

        .question-form-panel {
          width: 600px;
          max-width: 100%;
          max-height: 90vh;
          background: white;
          border-radius: 12px;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
          display: flex;
          flex-direction: column;
          animation: slideUp 0.3s ease;
          overflow: hidden;
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .question-form-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid #e5e7eb;
          background: linear-gradient(135deg, #b91c1c 0%, #dc2626 100%);
          color: white;
        }

        .question-form-header h3 {
          margin: 0;
          font-size: 20px;
        }

        .close-btn {
          background: rgba(255, 255, 255, 0.2);
          border: none;
          color: white;
          font-size: 24px;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
        }

        .close-btn:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: rotate(90deg);
        }

        .question-form-body {
          padding: 24px;
          overflow-y: auto;
          flex: 1;
        }

        .option-input-group {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 10px;
        }

        .option-input-group input[type="text"] {
          flex: 1;
        }

        .option-input-group input[type="radio"],
        .option-input-group input[type="checkbox"] {
          width: 20px;
          height: 20px;
          cursor: pointer;
        }

        .remove-option-btn {
          background: #ef4444;
          color: white;
          border: none;
          border-radius: 50%;
          width: 28px;
          height: 28px;
          cursor: pointer;
          font-size: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
        }

        .remove-option-btn:hover {
          background: #dc2626;
        }

        .add-option-btn {
          padding: 8px 16px;
          background: #f3f4f6;
          color: #374151;
          border: 2px dashed #d1d5db;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          margin-top: 8px;
        }

        .add-option-btn:hover {
          background: #e5e7eb;
          border-color: #b91c1c;
          color: #b91c1c;
        }

        .question-form-footer {
          display: flex;
          gap: 12px;
          padding: 20px 24px;
          border-top: 1px solid #e5e7eb;
          background: #f9fafb;
        }
      `}</style>
    </div>
  );
};

export default Quizzes;
